import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type { DiagramParentType } from '~/apollo/generated/v4/graphql';
import { Button } from '~/components/ui/button';
import { useRefetchQueriesV4 } from '~/hooks/apollo';
import { imageMimes } from '~/utils/formik';
import type { RefetchQueries } from '~/utils/graphql';
import { uploadFile } from '~/utils/modules/file';

const CREATE_DIAGRAM_IMAGE = graphql(`
  mutation CreateDiagram($input: DiagramCreateInput!) {
    diagramCreate(input: $input) {
      token
      path
    }
  }
`);

type Props = {
  parentType: DiagramParentType;
  parentId: number;
  refetchQueries: RefetchQueries;
};

export function UploadDiagramImage(props: Props) {
  // Set up refetchQueries here outside of the v4 provider
  // Once the v4 provider can be removed, this can be merged into one component
  const [refetch] = useRefetchQueriesV4(props.refetchQueries);

  async function handleUploadSuccess() {
    await refetch();
  }

  return (
    <ApolloProviderV4>
      <UploadDiagramImageFormInner
        {...props}
        onUploadSuccess={handleUploadSuccess}
      />
    </ApolloProviderV4>
  );
}

function UploadDiagramImageFormInner({
  parentType,
  parentId,
  onUploadSuccess,
}: Omit<Props, 'refetchQueries'> & {
  onUploadSuccess: () => Promise<void>;
}) {
  const [loading, setLoading] = useState(false);
  const [createDiagram] = useMutation(CREATE_DIAGRAM_IMAGE);
  const [file, setFile] = useState<File | null>(null);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!file) {
      return void toast.error('An image is required.');
    }

    try {
      setLoading(true);
      const res = await createDiagram({
        variables: {
          input: {
            parentType,
            parentId,
          },
        },
      });
      const path = res.data?.diagramCreate?.path;
      const token = res.data?.diagramCreate?.token;
      invariant(path && token, 'Missing upload path or token');

      const uploadResult = await uploadFile({ path, token, file });
      if (!uploadResult) {
        throw new Error('POST file failed');
      }

      await onUploadSuccess();
      toast.success('File uploaded successfully.');
      setLoading(false);
    } catch (err) {
      toast.error('An error occurred uploading the image.');
      console.log('Error uploading image', err);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <input
        type="file"
        name="file"
        accept={imageMimes.join(',')}
        onChange={e => setFile(e.target.files?.[0] || null)}
        className="file-input w-full"
      />

      <div className="text-center">
        <Button type="submit" color="primary" loading={loading}>
          Upload
        </Button>
      </div>
    </form>
  );
}
